import type { StateTree, PIIConsistencyResponseItem } from '@makemydeal/dr-dash-types';
import { getIsEcommerceDeal } from './dealXgDetailsSelectors';
import { getShopperInfo } from './offerRedux';

export const getDealSpd = (state: StateTree) => {
    return state.dealSpd?.data || null;
};

export const getPIIConsistency = (state: StateTree): PIIConsistencyResponseItem | null => {
    return (state.piiConsistency?.getPIIResponse as PIIConsistencyResponseItem) || null;
};

export const getPIIConsistencyLoading = (state: StateTree): boolean => {
    return state.piiConsistency?.getPIILoading || false;
};

export const getPIIConsistencyError = (state: StateTree) => {
    return state.piiConsistency?.getPIIError || null;
};

export const getComplianceIsLoading = (state: StateTree) => {
    return state.dealSpd.isLoading || false;
};

export const shouldShowComplianceElements = (state: StateTree) => {
    const isComplianceLoading = getComplianceIsLoading(state);
    const isEcommerceDeal = getIsEcommerceDeal(state);
    return !isComplianceLoading && isEcommerceDeal;
};

export const getComplianceParticipantForShopper = (state: StateTree) => {
    const isEcommerceDeal = getIsEcommerceDeal(state);
    if (!isEcommerceDeal) return null;
    const dealSpd = getDealSpd(state) || [];
    const participants = dealSpd.participantsSPD || [];
    const shopperId = getShopperInfo(state).commonConsumerId || '';
    if (!Array.isArray(participants) || !shopperId) return null;
    return (
        participants.find((participant) => {
            return participant?.commonConsumerId === shopperId;
        }) || null
    );
};

export const getComplianceParticipantPIIData = (state: StateTree): PIIConsistencyResponseItem | null => {
    const isEcommerceDeal = getIsEcommerceDeal(state);
    if (!isEcommerceDeal) return null;
    return getPIIConsistency(state);
};

// Helper selector to determine overall PII consistency status
export const getPIIConsistencyStatus = (state: StateTree): 'PASS' | 'FAIL' | 'LOADING' | 'ERROR' => {
    if (getPIIConsistencyLoading(state)) return 'LOADING';

    const error = getPIIConsistencyError(state);
    if (error) return 'ERROR';

    const piiData = getPIIConsistency(state);
    if (!piiData) return 'ERROR';

    // Check if any field has equal: false
    const hasInconsistencies = Object.values(piiData).some((field) => field && !field.equal);
    return hasInconsistencies ? 'FAIL' : 'PASS';
};

// Helper selector to get inconsistent fields
export const getInconsistentPIIFields = (state: StateTree): string[] => {
    const piiData = getPIIConsistency(state);
    if (!piiData) return [];

    return Object.entries(piiData)
        .filter(([_, field]) => field && !field.equal)
        .map(([fieldName]) => fieldName);
};

// Helper selector to get error message with proper formatting
export const getPIIConsistencyErrorMessage = (state: StateTree): string | null => {
    const error = getPIIConsistencyError(state);
    if (!error) return null;

    // Handle different error formats
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.rawError) return error.rawError;

    return 'An unknown error occurred while checking PII consistency';
};

// Resend ID Verification selectors
export const getResendIdVerificationData = (state: StateTree) => {
    return state.resendIdVerification?.data || null;
};

export const getResendIdVerificationLoading = (state: StateTree): boolean => {
    return state.resendIdVerification?.isLoading || false;
};

export const getResendIdVerificationError = (state: StateTree) => {
    return state.resendIdVerification?.error || null;
};
